import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import PlaceIcon from '@mui/icons-material/Place';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { deriveBriefing } from '../campaign-wizard.api';
import {
  applyRederivation,
  derivationKey,
  derivationStale,
  isLinkedinUrl,
  searchLinkRows,
  withSearchLinks,
} from '../campaign-wizard.logic';
import { GeoOption, MAX_SEARCH_LINKS, RADIUS_STEPS, SearchCriteria, SearchMethod } from '../campaign-wizard.types';
import { wz } from '../components/wizard-tokens';
import {
  WizAddChip,
  WizCard,
  WizChip,
  WizChoiceCard,
  WizHeading,
  WizInput,
  WizLabel,
  WizLink,
  WizOutlineButton,
  WizRadioIcon,
  WizSection,
  MiSpinner,
} from '../components/wizard-ui';
import BlacklistCard from './candidates/blacklist-card';
import {
  addPostalOption,
  applyLocationOption,
  canEditRadius,
  effectiveRadius,
  isPostalLocation,
  isUnconfirmed,
  locationHintKey,
  locationLabel,
  radiusLabel,
  removeLocation,
  searchTextOf,
  setLocationRadius,
  supportsPostalRadius,
  supportsRadius,
} from './candidates/candidates-logic';
import CriteriaRow from './candidates/criteria-row';
import IndustryPicker from './candidates/industry-picker';
import LocationPicker from './candidates/location-picker';
import type { WizardStepProps } from './step.types';

const CRITERIA_ROWS: Array<{ key: keyof SearchCriteria; labelKey: string }> = [
  { key: 'jobTitles', labelKey: 'wizCandCritJobTitles' },
  // Schlüssel `experience`, Anzeige „Weitere Suchparameter“: die Chips gehen als Stichworte in die LinkedIn-Suche.
  { key: 'experience', labelKey: 'wizCandCritExperience' },
  { key: 'languages', labelKey: 'wizCandCritLanguages' },
];

/** Schritt 2 „Kandidaten“: Suche (SAIA oder eigener Link) und Blacklist. */
const StepCandidates: React.FC<WizardStepProps> = ({ wizard }) => {
  const { classes } = useStyles();
  const { t, i18n } = useTranslation();
  const { state, patch, account, showBlocker, blocker } = wizard;
  const [radiusMenu, setRadiusMenu] = useState<{ anchor: HTMLElement; index: number } | null>(null);
  const [editing, setEditing] = useState(false);
  /** Ortssuche offen: `index` = Vorschlag, der bestätigt wird; `null` = neuer Ort. */
  const [picker, setPicker] = useState<{ index: number | null } | null>(null);
  /** Umschalter für radiusfähige Konten (Sales Navigator / Recruiter): „Ort“ oder „PLZ + Umkreis“. */
  const [locMode, setLocMode] = useState<'city' | 'postal'>('city');
  const radiusSupported = supportsRadius(account?.productType);
  const postalSupported = supportsPostalRadius(account?.productType);
  const [rederiving, setRederiving] = useState(false);
  const [rederiveError, setRederiveError] = useState(false);
  const stale = derivationStale(state);
  /** Das Radius-Menü gehört zu einem PLZ-Ort: dort gibt es kein „Ganze Region“. */
  const menuLocation = radiusMenu ? state.locations[radiusMenu.index] : undefined;
  const menuPostal = !!menuLocation && isPostalLocation(menuLocation);

  // Überschreibt Orte/Suchbegriffe/Branchen erst nach dem Klick (nie stillschweigend).
  const rederive = async () => {
    setRederiving(true);
    setRederiveError(false);
    try {
      const derived = await deriveBriefing(state.briefing, { extras: state.briefingExtras });
      patch(applyRederivation(state, derived));
    } catch {
      setRederiveError(true);
    } finally {
      setRederiving(false);
    }
  };

  const methods: Array<{ key: SearchMethod; icon: React.ReactNode; title: string; desc: string }> =
    [
      {
        key: 'saia',
        icon: <AutoAwesomeIcon sx={{ fontSize: 22, color: wz.primary }} />,
        title: t('wizCandMethodSaiaTitle'),
        desc: t('wizCandMethodSaiaDesc'),
      },
      {
        key: 'url',
        icon: <LinkIcon sx={{ fontSize: 22, color: wz.primary }} />,
        title: t('wizCandMethodUrlTitle'),
        desc: t('wizCandMethodUrlDesc'),
      },
    ];

  const pickLocation = (option: GeoOption, query: string) => {
    if (!picker) return;
    patch({
      locations:
        locMode === 'postal' && picker.index === null && postalSupported
          ? addPostalOption(state.locations, option, query)
          : applyLocationOption(state.locations, picker.index, option, radiusSupported),
    });
    setPicker(null);
  };

  const locationPicker = (index: number | null, initial: string) => (
    <LocationPicker
      key={`picker-${index ?? 'new'}`}
      accountId={state.accountId}
      initial={initial}
      mode={index === null && locMode === 'postal' && postalSupported ? 'postal' : 'city'}
      productType={account?.productType}
      onSelect={pickLocation}
      onCancel={() => setPicker(null)}
    />
  );

  // Eigene Links: findet jeder bei LinkedIn jemanden? (Prüfung je Link im Hintergrund, siehe useCampaignWizard)
  const linkRows = searchLinkRows(state);
  const setLinkRows = (rows: string[]) => patch(withSearchLinks(rows));
  const checkOf = (url: string) =>
    isLinkedinUrl(url) ? wizard.linkChecks[url.trim()] : undefined;
  const checkLine = (url: string) => {
    if (url.trim() !== '' && !isLinkedinUrl(url)) {
      return <div className={classes.error}>{t('wizCandUrlInvalid')}</div>;
    }
    const e = checkOf(url);
    if (!e || e.status === 'idle') return null;
    if (e.status === 'loading') {
      return (
        <div className={classes.linkLine}>
          <MiSpinner size={14} color={wz.muted} />
          {t('wizCandUrlChecking')}
        </div>
      );
    }
    if (e.link === 'works') {
      return (
        <div className={`${classes.linkLine} ${classes.linkOk}`}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          {e.value !== null
            ? t('wizCandUrlWorksCount', { count: e.value, formatted: e.value.toLocaleString(i18n.language) })
            : t('wizCandUrlWorks')}
        </div>
      );
    }
    if (e.link === 'empty' || e.link === 'broken') {
      return (
        <div className={`${classes.linkLine} ${classes.error}`}>
          <ErrorOutlineIcon sx={{ fontSize: 16 }} />
          {e.message}
        </div>
      );
    }
    return e.message ? <div className={classes.locHint}>{e.message}</div> : null;
  };
  // Zusammenfassung erst ab zwei Links: wie viele funktionieren und wie die ersten 50 verteilt werden.
  const validLinks = linkRows.filter(isLinkedinUrl);
  const workingChecks = validLinks.map(checkOf).filter(c => c?.link === 'works');
  const workingCount = validLinks.length - validLinks.map(checkOf).filter(c => c?.link === 'broken' || c?.link === 'empty').length;
  const workingTotal = workingChecks.reduce((sum, c) => sum + (c?.value ?? 0), 0);
  const linkSummary =
    validLinks.length >= 2 ? (
      <div className={classes.linkSummary}>
        <div style={{ fontSize: 14 }}>
          {workingTotal > 0
            ? t('wizCandUrlsTotal', { formatted: workingTotal.toLocaleString(i18n.language), count: workingCount })
            : t('wizCandUrlsWorking', { count: workingCount })}
        </div>
        <div style={{ fontSize: 13, color: wz.text555 }}>
          {t('wizCandUrlsSplit', { share: workingCount > 0 ? Math.round(50 / workingCount) : 50 })}
        </div>
      </div>
    ) : null;

  const urlMissing = showBlocker && blocker === 'searchUrl';
  const locationsInvalid = showBlocker && blocker === 'locations';

  return (
    <>
      <WizCard>
        <WizHeading title={t('wizCandTitle')} subtitle={t('wizCandSubtitle')} />
        {wizard.searchRan ? (
          <div style={{ fontSize: 13, color: wz.warnFg }} data-testid="wiz-cand-searched-hint">
            {t('wizSearchedHint')}
          </div>
        ) : null}
        <div className={classes.methods}>
          {methods.map(m => {
            const selected = state.method === m.key;
            return (
              <WizChoiceCard
                key={m.key}
                selected={selected}
                onClick={() => patch({ method: m.key })}
              >
                <div className={classes.methodTop}>
                  {m.icon}
                  <WizRadioIcon checked={selected} />
                </div>
                <div className={classes.methodTitle}>{m.title}</div>
                <div className={classes.methodDesc}>{m.desc}</div>
              </WizChoiceCard>
            );
          })}
        </div>

        {state.method === 'saia' && stale ? (
          <div className={classes.rederive} role="note" data-testid="wiz-rederive">
            <span>{t('wizCandRederiveHint')}</span>
            <div className={classes.rederiveActions}>
              <WizOutlineButton onClick={() => void rederive()} disabled={rederiving}>
                {rederiving ? t('wizCandRederiveBusy') : t('wizCandRederiveButton')}
              </WizOutlineButton>
              <WizLink onClick={() => patch({ derivedKey: derivationKey(state) })}>
                {t('wizCandRederiveKeep')}
              </WizLink>
            </div>
            {rederiveError ? <span className={classes.rederiveError}>{t('wizCandRederiveError')}</span> : null}
          </div>
        ) : null}

        {state.method === 'saia' ? (
          <WizSection gap={14}>
            <div className={classes.derivedHead}>
              <div className={classes.derivedTitle}>{t('wizCandDerived')}</div>
              <WizLink onClick={() => setEditing(e => !e)}>
                {editing ? t('wizCandAdjustDone') : t('wizCandAdjust')}
              </WizLink>
            </div>
            <div className={classes.locRow}>
              <div className={classes.rowLabel}>{t('wizCandLocations')}</div>
              <div className={classes.locBody}>
                {postalSupported ? (
                  <div className={classes.modeSwitch} role="group" data-testid="wiz-loc-mode">
                    {(['city', 'postal'] as const).map(m => (
                      <button
                        key={m}
                        type="button"
                        aria-pressed={locMode === m}
                        className={`${classes.modeButton} ${locMode === m ? classes.modeButtonOn : ''}`}
                        onClick={() => {
                          setLocMode(m);
                          setPicker(null);
                        }}
                      >
                        {m === 'city' ? t('wizCandLocModeCity') : t('wizCandLocModePostal')}
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className={classes.locChips}>
                  {state.locations.map((l, i) => {
                    if (picker?.index === i) return locationPicker(i, searchTextOf(l.city));
                    if (isUnconfirmed(l)) {
                      return (
                        <WizChip
                          key={`${l.city}-${i}`}
                          variant="warn"
                          icon={<PlaceIcon sx={{ fontSize: 14 }} />}
                          title={t('wizCandLocationConfirmTitle')}
                          onClick={() => setPicker({ index: i })}
                          onRemove={() => patch({ locations: removeLocation(state.locations, i) })}
                        >
                          {t('wizCandLocationConfirm', { city: l.city })}
                        </WizChip>
                      );
                    }
                    const radius = effectiveRadius(l, radiusSupported, postalSupported);
                    const postal = isPostalLocation(l);
                    return (
                      <WizChip
                        key={`${l.geoUrn}-${i}`}
                        variant="blue"
                        title={postal ? l.city : undefined}
                        icon={<PlaceIcon sx={{ fontSize: 14 }} />}
                        suffix={
                          radius === null
                            ? t('wizCandRadiusWholeRegion')
                            : radiusLabel(radius, i18n.language)
                        }
                        onSuffixClick={
                          canEditRadius(l, radiusSupported, postalSupported)
                            ? e => setRadiusMenu({ anchor: e.currentTarget, index: i })
                            : undefined
                        }
                        onRemove={() => patch({ locations: removeLocation(state.locations, i) })}
                      >
                        {locationLabel(l)}
                      </WizChip>
                    );
                  })}
                  {picker?.index === null ? (
                    locationPicker(null, '')
                  ) : (
                    <WizAddChip
                      label={
                        locMode === 'postal' && postalSupported
                          ? t('wizCandAddPostal')
                          : t('wizCandAddLocation')
                      }
                      onClick={() => setPicker({ index: null })}
                    />
                  )}
                </div>
                <div
                  className={classes.locHint}
                  style={locationsInvalid ? { color: wz.redFg } : undefined}
                >
                  {locationsInvalid
                    ? t('wizMissingLocations')
                    : t(locationHintKey({ locMode, postalSupported, radiusSupported }))}
                </div>
                {account && !postalSupported ? (
                  <div className={classes.locHint}>{t('wizCandRadiusNote')}</div>
                ) : null}
                {account && postalSupported && !radiusSupported && locMode === 'city' ? (
                  <div className={classes.locHint}>{t('wizCandRadiusNotePostal')}</div>
                ) : null}
              </div>
              <Menu
                open={radiusMenu !== null}
                anchorEl={radiusMenu?.anchor}
                onClose={() => setRadiusMenu(null)}
              >
                {RADIUS_STEPS.map(step => (
                  <MenuItem
                    key={step}
                    dense
                    selected={radiusMenu !== null && state.locations[radiusMenu.index]?.radius === step}
                    onClick={() => {
                      if (radiusMenu)
                        patch({ locations: setLocationRadius(state.locations, radiusMenu.index, step) });
                      setRadiusMenu(null);
                    }}
                  >
                    {t('wizCandRadiusOption', { km: step.toLocaleString(i18n.language) })}
                  </MenuItem>
                ))}
                {menuPostal ? null : <Divider />}
                {menuPostal ? null : (
                  <MenuItem
                    dense
                    selected={radiusMenu !== null && state.locations[radiusMenu.index]?.radius === null}
                    onClick={() => {
                      if (radiusMenu)
                        patch({ locations: setLocationRadius(state.locations, radiusMenu.index, null) });
                      setRadiusMenu(null);
                    }}
                  >
                    {t('wizCandRadiusWholeRegionOption')}
                  </MenuItem>
                )}
              </Menu>
            </div>
            {CRITERIA_ROWS.map(row => (
              <CriteriaRow
                key={row.key}
                label={t(row.labelKey)}
                values={state.criteria[row.key]}
                editing={editing}
                onChange={values => patch({ criteria: { ...state.criteria, [row.key]: values } })}
              />
            ))}
            <div className={classes.industryRow}>
              <div className={classes.rowLabel}>{t('wizCandCritIndustries')}</div>
              <div className={classes.industryBody}>
                <IndustryPicker
                  values={state.criteria.industries}
                  onChange={industries => patch({ criteria: { ...state.criteria, industries } })}
                />
                <div className={classes.locHint}>{t('wizCandIndustryHint')}</div>
              </div>
            </div>
          </WizSection>
        ) : (
          <WizSection gap={14}>
            <WizLabel>{t(linkRows.length > 1 ? 'wizCandUrlsLabel' : 'wizCandUrlLabel')}</WizLabel>
            {linkRows.map((url, i) => (
              <div key={i} className={classes.linkRow}>
                <div className={classes.linkInputRow}>
                  {linkRows.length > 1 ? <span className={classes.linkNumber}>{i + 1}</span> : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <WizInput
                      value={url}
                      onChange={value => setLinkRows(linkRows.map((r, j) => (j === i ? value : r)))}
                      placeholder={t('wizCandUrlPlaceholder')}
                      invalid={(urlMissing && !isLinkedinUrl(url)) || (url.trim() !== '' && !isLinkedinUrl(url))}
                      aria-label={linkRows.length > 1 ? t('wizCandUrlRowLabel', { n: i + 1 }) : t('wizCandUrlLabel')}
                    />
                  </div>
                  {linkRows.length > 1 ? (
                    <button
                      type="button"
                      className={classes.linkRemove}
                      aria-label={t('wizCandUrlRemove', { n: i + 1 })}
                      title={t('wizCandUrlRemove', { n: i + 1 })}
                      onClick={() => setLinkRows(linkRows.filter((_, j) => j !== i))}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </button>
                  ) : null}
                </div>
                <div style={{ paddingLeft: linkRows.length > 1 ? 24 : 0 }}>{checkLine(url)}</div>
              </div>
            ))}
            {linkRows.length < MAX_SEARCH_LINKS ? (
              <div className={classes.linkAdd} style={{ paddingLeft: linkRows.length > 1 ? 24 : 0 }}>
                <WizOutlineButton onClick={() => setLinkRows([...linkRows, ''])} icon={<AddIcon sx={{ fontSize: 16 }} />}>
                  {t('wizCandUrlAdd')}
                </WizOutlineButton>
                <span className={classes.locHint}>{t('wizCandUrlMax', { count: MAX_SEARCH_LINKS })}</span>
              </div>
            ) : null}
            {linkSummary}
          </WizSection>
        )}
      </WizCard>
      <BlacklistCard wizard={wizard} />
    </>
  );
};

const useStyles = makeStyles()({
  methods: { display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 12 },
  methodTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  methodTitle: { fontSize: 15, fontWeight: 700 },
  methodDesc: { fontSize: 13, color: wz.muted, lineHeight: 1.4 },
  rederive: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    fontSize: 14,
    fontWeight: 600,
    color: wz.textDim,
    background: wz.primarySoft,
    borderLeft: `3px solid ${wz.primary}`,
    borderRadius: 8,
    padding: '10px 14px',
  },
  rederiveActions: { display: 'flex', alignItems: 'center', gap: 16 },
  rederiveError: { fontSize: 13, fontWeight: 400, color: wz.redFg },
  derivedHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  derivedTitle: { fontSize: 15, fontWeight: 700 },
  locRow: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  rowLabel: { width: 160, fontSize: 14, color: wz.muted, paddingTop: 4, flexShrink: 0 },
  locBody: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  modeSwitch: { display: 'inline-flex', alignSelf: 'flex-start', gap: 0, border: `1px solid ${wz.border}`, borderRadius: 8, overflow: 'hidden' },
  modeButton: {
    height: 28,
    padding: '0 12px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    color: wz.textDim,
    background: '#FFF',
    border: 'none',
  },
  modeButtonOn: { color: wz.primary, background: wz.primarySoft },
  locChips: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 },
  locHint: { fontSize: 12, color: wz.muted },
  industryRow: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  industryBody: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  error: { fontSize: 13, color: wz.redFg },
  linkLine: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: wz.muted },
  linkRow: { display: 'flex', flexDirection: 'column', gap: 6 },
  linkInputRow: { display: 'flex', alignItems: 'center', gap: 8 },
  linkNumber: { width: 16, fontSize: 12, color: wz.muted, textAlign: 'right' },
  linkRemove: {
    width: 44,
    height: 44,
    flexShrink: 0,
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    color: wz.muted,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': { background: wz.page, color: wz.redFg },
  },
  linkAdd: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  linkSummary: {
    borderTop: `1px solid ${wz.borderSoft}`,
    paddingTop: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  linkOk: { color: wz.success },
});

export default StepCandidates;
